import { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../services/companyAdminService";
import { useApp } from "../../context/AppContext";
import { useGetProfileQuery } from "../../store/companyAdminApi";
import { selectToken, selectCompanyUser, selectCompany, clearCredentials } from "../../store/authSlice";
import {
  Dashboard as OverviewIcon,
  Settings as SettingsIcon,
  Description as ContractsIcon,
  People as ClientsIcon,
  Webhook as WebhooksIcon,
  MenuBook as DocsIcon,
  AccountBalanceWallet as UsageIcon,
  Logout as LogoutIcon,
  LightMode as SunIcon,
  DarkMode as MoonIcon,
} from "@mui/icons-material";
import { Avatar } from "@mui/material";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Button,
  Menu,
  MenuItem,
} from "@/components/ui";
import PlanChip from "../../components/PlanChip";
import { createDashboardTheme } from "../../theme/dashboardTheme";

const DRAWER_WIDTH = 260;

const listItemIconSx = {
  minWidth: 40,
  color: "inherit",
  opacity: 0.85,
  "& svg": { width: 20, height: 20 },
};

const listItemTextSx = (isRTL) => ({
  "& .MuiListItemText-primary": { textAlign: isRTL ? "right" : "left" },
});

const NavItemContent = ({ icon: Icon, label, isRTL }) => {
  const iconEl = (
    <ListItemIcon sx={listItemIconSx}>
      <Icon />
    </ListItemIcon>
  );
  const textEl = (
    <ListItemText
      primary={label}
      primaryTypographyProps={{
        fontSize: "0.875rem",
        fontWeight: 500,
      }}
      sx={listItemTextSx(isRTL)}
    />
  );
  if (isRTL) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", width: "100%", direction: "rtl" }}>
        {iconEl}
        {textEl}
      </Box>
    );
  }
  return (
    <>
      {iconEl}
      {textEl}
    </>
  );
};

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t, isRTL, isDark, toggleTheme, language, setLanguage } = useApp();
  const token = useSelector(selectToken);
  const user = useSelector(selectCompanyUser);
  const company = useSelector(selectCompany);
  const { data: profile } = useGetProfileQuery(undefined, { skip: !token });
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const langMenuOpen = Boolean(langMenuAnchor);

  const companyName = profile?.companyName ?? company?.name ?? "Company";
  const logoUrl = profile?.branding?.logoUrl ?? null;
  const subscriptionPlan = profile?.subscription?.plan ?? company?.plan ?? "professional";
  const email = user?.email ?? profile?.contactEmail ?? "";

  const handleLangMenuOpen = (e) => setLangMenuAnchor(e.currentTarget);
  const handleLangMenuClose = () => setLangMenuAnchor(null);
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    handleLangMenuClose();
  };

  const langLabel = language === "he" ? "עברית" : "English";

  const theme = createDashboardTheme(
    isDark ? "dark" : "light",
    isRTL ? "rtl" : "ltr"
  );

  const handleLogout = () => {
    logout();
    dispatch(clearCredentials());
    navigate("/company/login");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Keep layout direction LTR so flex sizes correctly; RTL only on main content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isRTL ? "row-reverse" : "row",
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100vw",
          overflow: "hidden",
          bgcolor: "background.default",
          direction: "ltr",
        }}
      >
        <Drawer
          variant="permanent"
          anchor={isRTL ? "right" : "left"}
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              mt: 0,
              pt: 1,
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
              ...(isRTL
                ? { borderLeft: 1, borderRight: 0, borderColor: "divider" }
                : { borderRight: 1, borderColor: "divider" }),
            },
          }}
        >
          {/* Sidebar header: image → company name → plan (stacked, centered) */}
          <Box
            sx={{
              px: 2,
              py: 2,
              pb: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1,
            }}
          >
            <Avatar
              src={logoUrl || undefined}
              alt=""
              sx={{ width: 44, height: 44, flexShrink: 0, bgcolor: "primary.main" }}
            >
              {companyName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography
              variant="body2"
              color="text.primary"
              fontWeight={600}
              noWrap
              sx={{
                fontSize: "0.8125rem",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {companyName}
            </Typography>
            <PlanChip
              plan={subscriptionPlan}
              sx={{ py: 0.25, fontSize: "0.75rem" }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {email}
            </Typography>
          </Box>

          {/* Main nav */}
          <List sx={{ px: 1, py: 1, flex: 1 }}>
            <ListItemButton
              selected={isActive("/company/dashboard")}
              onClick={() => navigate("/company/dashboard")}
            >
              <NavItemContent
                icon={OverviewIcon}
                label={t("overview")}
                isRTL={isRTL}
              />
            </ListItemButton>
            <ListItemButton
              selected={isActive("/company/settings")}
              onClick={() => navigate("/company/settings")}
            >
              <NavItemContent
                icon={SettingsIcon}
                label={t("settings")}
                isRTL={isRTL}
              />
            </ListItemButton>

            <Divider sx={{ my: 1 }} />

            <ListItemButton
              selected={isActive("/company/contracts")}
              onClick={() => navigate("/company/contracts")}
            >
              <NavItemContent
                icon={ContractsIcon}
                label={t("contracts")}
                isRTL={isRTL}
              />
            </ListItemButton>
            <ListItemButton
              selected={isActive("/company/clients")}
              onClick={() => navigate("/company/clients")}
            >
              <NavItemContent
                icon={ClientsIcon}
                label={t("clients")}
                isRTL={isRTL}
              />
            </ListItemButton>
            <ListItemButton
              selected={isActive("/company/templates")}
              onClick={() => navigate("/company/templates")}
            >
              <NavItemContent
                icon={DocsIcon}
                label={t("templates")}
                isRTL={isRTL}
              />
            </ListItemButton>
            <ListItemButton
              selected={isActive("/company/webhooks")}
              onClick={() => navigate("/company/webhooks")}
            >
              <NavItemContent
                icon={WebhooksIcon}
                label={t("webhooks")}
                isRTL={isRTL}
              />
            </ListItemButton>
            <ListItemButton
              selected={isActive("/company/usage")}
              onClick={() => navigate("/company/usage")}
            >
              <NavItemContent
                icon={UsageIcon}
                label={t("usageBilling")}
                isRTL={isRTL}
              />
            </ListItemButton>

            <Divider sx={{ my: 1 }} />

            <ListItemButton onClick={() => navigate("/docs/api")}>
              <NavItemContent icon={DocsIcon} label={t("docs")} isRTL={isRTL} />
            </ListItemButton>
          </List>

          {/* Bottom: language (75%) before Color mode (25%), then logout */}
          <Box
            sx={{
              p: 1.5,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              alignItems: isRTL ? "flex-start" : "flex-end",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                gap: 1,
                mb: 1,
                width: "100%",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <Box sx={{ width: "75%" }}>
                <Button
                  fullWidth
                  size="small"
                  onClick={handleLangMenuOpen}
                  aria-controls={langMenuOpen ? "lang-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={langMenuOpen ? "true" : undefined}
                  dir={language === "he" ? "rtl" : "ltr"}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "action.hover",
                    color: "text.primary",
                    justifyContent: "space-between",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "action.selected",
                      borderColor: "divider",
                    },
                  }}
                >
                  <span>{langLabel}</span>
                  <Typography
                    component="span"
                    sx={{ opacity: 0.7 }}
                    aria-hidden
                  >
                    ▾
                  </Typography>
                </Button>
                <Menu
                  id="lang-menu"
                  anchorEl={langMenuAnchor}
                  open={langMenuOpen}
                  onClose={handleLangMenuClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: isRTL ? "right" : "left",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: isRTL ? "right" : "left",
                  }}
                  slotProps={{ paper: { sx: { minWidth: 140 } } }}
                >
                  <MenuItem
                    onClick={() => handleLanguageChange("he")}
                    selected={language === "he"}
                  >
                    🇮🇱 עברית
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleLanguageChange("en")}
                    selected={language === "en"}
                  >
                    🇺🇸 English
                  </MenuItem>
                </Menu>
              </Box>
              <Box
                sx={{
                  width: "25%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  size="small"
                  onClick={toggleTheme}
                  title={isDark ? t("lightMode") : t("darkMode")}
                  sx={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    minHeight: 32,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "action.hover",
                    "&:hover": { bgcolor: "action.selected" },
                    "& svg": { width: 18, height: 18 },
                  }}
                >
                  {isDark ? <SunIcon /> : <MoonIcon />}
                </IconButton>
              </Box>
            </Box>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 1,
                width: "fit-content",
                alignSelf: isRTL ? "flex-end" : "flex-start",
                gap: isRTL ? 0.5 : 1,
                padding: isRTL ? "8px 8px 8px 8px" : "8px 8px 8px 16px",
                minHeight: 40,
              }}
              dir={isRTL ? "rtl" : "ltr"}
            >
              {isRTL ? (
                <>
                  <ListItemIcon
                    sx={{
                      ...listItemIconSx,
                      minWidth: 28,
                    }}
                  >
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("logout")}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                    sx={{
                      flex: "none",
                      "& .MuiListItemText-primary": { textAlign: "right" },
                    }}
                  />
                </>
              ) : (
                <>
                  <ListItemText
                    primary={t("logout")}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                    sx={{
                      flex: "none",
                      "& .MuiListItemText-primary": { textAlign: "left" },
                    }}
                  />
                  <ListItemIcon
                    sx={{
                      ...listItemIconSx,
                      minWidth: 28,
                    }}
                  >
                    <LogoutIcon />
                  </ListItemIcon>
                </>
              )}
            </ListItemButton>
          </Box>
        </Drawer>

        <Box
          component="main"
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            width: 0, // with flex: 1 1 0 ensures main takes only remaining space
            maxWidth: `calc(100vw - ${DRAWER_WIDTH}px)`,
            p: 3,
            overflow: "auto",
            minHeight: "100vh",
            boxSizing: "border-box",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardLayout;
